import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import { APIRequestConfig } from '../api/APIConfigs';
import { Environment } from '../Environment';

/**
 * Use this hook to get a function that can be used to make
 * authenticated API calls from inside functional components.
 * Example:
 *
 * import { apiConfigs } from '../api/APIConfigs.ts';  // Request structure is defined here.
 *
 * const MyAwesomeComponent = () => {
 *   const request = useRequest();
 *   ...
 *   request(apiConfigs.getBibleStudies('id'))
 *     .then((response) => {
 *       Handle response here
 *       ...
 *     });
 * }
 *
 * @returns
 */
export const useRequest = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const {
    services: { logger },
  } = Environment.current();

  // memoized the function, as otherwise if the hook is used inside
  // a useEffect, it will lead to an infinite loop
  const request = useCallback(
    async <T>(config: APIRequestConfig): Promise<T> => {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      });
      console.log('API TOKEN:', token);
      const formData = new FormData();
      if (config.upload && config.data) {
        const file = config.data as File;
        formData.append('', file, file.name);
      }

      const init: AxiosRequestConfig = {
        url: `${process.env.REACT_APP_API_BASE_URL}${config.url}`,
        method: config.method,
        data: config.upload ? formData : config.data,
        headers: {
          'Content-Type': config.upload ? 'multipart/form-data' : 'application/json',
          Authorization: `Bearer ${isAuthenticated ? token : 'anonymous'}`,
          'ngrok-skip-browser-warning': 'true',
        },
      };

      const url0 = '/' + config.url.split('/')[1].split('?')[0];
      logger.log('\n');
      logger.log(`${url0}\t [${init.method}] - ${url0}`);
      logger.log(`${url0}\t [Authorization]:`, init.headers.Authorization);
      logger.log(`${url0}\t [body]:`, init.data);

      return axios(init)
        .then(response => {
          logger.log(`${url0}\t [response]:`, response.data);
          return response.data;
        })
        .catch(function (error) {
          // TODO: handle request errors
          logger.log(`${url0}\t [error.json]:`, error.toJSON());
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            // logger.log(error.response.data);
            // logger.log(error.response.status);
            // logger.log(error.response.headers);

            logger.log(`${url0}\t [error.response]:`, error.response);
            const errorMessage =
              typeof error.response.data === 'string' ? error.response.data : error.response.data.innerError;

            throw new Error(errorMessage);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            logger.log(`${url0}\t [error.request]:`, error.request);
            throw new Error(`Unable to process request (err: ${error.message})`);
          } else {
            // Something happened in setting up the request that triggered an Error
            logger.log(`${url0}\t [error.message]:`, error.message);
            throw new Error(error.message);
          }
        });
    },
    [getAccessTokenSilently, isAuthenticated, logger]
  );

  return request;
};
